import { ResultOf } from 'gql.tada';
import { z } from 'zod';
import { pcaChatContextFragment } from '../../../../../graphql/propertyPageQuery';
import propertyImagePlaceholderSvg from '../../../../../../assets/property-image-placeholder.svg';
import {
  propertyAttributesSchema,
  propertyTileComponentSchema,
} from '../hooks/uiComponentsSchemas';
import { AgentMessage, Message, UiComponent } from '../hooks/clientMessages';

const getPropertyAttributeKey = (
  str: string,
): z.infer<typeof propertyAttributesSchema>['attributeType'][number] | null => {
  switch (str) {
    case 'bedrooms':
      return 'bedrooms';
    case 'bathrooms':
      return 'bathrooms';
    case 'car_spaces':
      return 'car_spaces';
    default:
      return null;
  }
};

const getPropertyAttributes = (
  attributes: ResultOf<typeof pcaChatContextFragment>['propertyAttributes'],
): z.infer<typeof propertyAttributesSchema>[] => {
  return attributes
    .map((attribute) => {
      const type = getPropertyAttributeKey(attribute.type);
      if (!type) return null;
      return {
        attributeType: getPropertyAttributeKey(attribute.type),
        value: attribute.value,
      };
    })
    .filter(Boolean) as z.infer<typeof propertyAttributesSchema>[];
};

const getImageUrl = (image: ResultOf<typeof pcaChatContextFragment>['mainImage']): string => {
  if (!image) return propertyImagePlaceholderSvg;
  if ('__typename' in image && image.__typename === 'PropertyPage_AerialImage') {
    return image.uri;
  }
  if ('__typename' in image && image.__typename === 'PropertyPage_ListingImage') {
    return image.mobileFullScreenImageJpgUri;
  }
  return propertyImagePlaceholderSvg;
};

export const createInitialMessages = ({
  pcaChatContext,
}: {
  pcaChatContext: ResultOf<typeof pcaChatContextFragment>;
}): Message => {
  const { propertyAttributes, propertyId, address, mainImage, propertyStatus } = pcaChatContext;

  const priorText = {
    componentType: 'text',
    text: 'Hey there, you’ve come to the right place to compare properties against each other. Do you want to start with this property?',
  } satisfies UiComponent;

  const propertyTile: z.infer<typeof propertyTileComponentSchema> = {
    componentType: 'property_tile',
    propertyId: propertyId,
    address,
    imageUrl: getImageUrl(mainImage),
    attributes: getPropertyAttributes(propertyAttributes),
    price: null,
    propertyStatus: propertyStatus?.toLowerCase() as z.infer<
      typeof propertyTileComponentSchema
    >['propertyStatus'],
  } satisfies UiComponent;

  const recommendationText = {
    componentType: 'client_action',
    actions: [
      {
        agentMessage: `
        Initial clientside rendered "agent" message context: Hey there, you’ve come to the right place to compare properties against each other. Do you want to start with this property? 
        
        User answers: Could you provide a list of properties similar to the one with address ${address} (property id - ${propertyId})?`,
        text: 'Choose another',
      },
      {
        agentMessage: `
        Initial clientside rendered "agent" message context: Hey there, you’ve come to the right place to compare properties against each other. Do you want to start with this property? 
        
        User answers: Can we start with the property with address ${address} (property id - ${propertyId})?`,
        text: 'Yes',
      },
    ],
  } satisfies UiComponent;

  return {
    sender: 'agent',
    invocationId: crypto.randomUUID(),
    parts: [
      {
        type: 'content',
        components: [priorText, propertyTile, recommendationText],
      },
    ],
  } satisfies AgentMessage;
};
